import { createApi } from "@reduxjs/toolkit/query/react";
import { setToken } from "../../features/userSlice";
import CustomFetchBase from "../CustomFetchBase";

export const UserApi = createApi({
  reducerPath: "UserApi",
  baseQuery: CustomFetchBase,
  tagTypes: ["User", "Users"],
  endpoints: (builder) => ({
    // Register new user
    registerUser: builder.mutation({
      query: (data) => ({
        url: "/users/register",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          console.log(data);

          if (data && data?.data?.token) {
            dispatch(setToken(data?.data?.token));
          }
        } catch (error) {}
      },
      invalidatesTags: ["Users"],
    }),

    // User login
    loginUser: builder.mutation({
      query: (data) => ({
        url: "/users/login",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          console.log(data);

          if (data && data?.data?.token) {
            dispatch(setToken(data?.data?.token));
          }
        } catch (error) {}
      },
      invalidatesTags: ["Users"],
    }),

    // Get all users with pagination (Admin only)
    getAllUsers: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        role = "",
        status = "",
      } = {}) => ({
        url: `/users?page=${page}&limit=${limit}&search=${search}&role=${role}&status=${status}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["Users"],
    }),

    // Get user by ID
    getUserById: builder.query({
      query: (id) => ({
        url: `/users/${id}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: (result, error, id) => [{ type: "User", id }],
    }),

    // Add new user (Admin only)
    addUser: builder.mutation({
      query: (data) => ({
        url: "/users/add",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["Users"],
    }),

    // Update user
    updateUser: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/users/${id}`,
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        "Users",
      ],
    }),

    // Edit user (partial update)
    editUser: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/users/${id}`,
        method: "PATCH",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        "Users",
      ],
    }),

    // Delete user (Admin only)
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `/users/${id}`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["Users"],
    }),

    // Forgot password
    forgotPassword: builder.mutation({
      query: (data) => ({
        url: "/users/forgot-password",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["UserApi"],
    }),

    // Reset password
    resetPassword: builder.mutation({
      query: ({ token, password }) => ({
        url: `/users/reset-password/${token}`,
        method: "POST",
        body: { password },
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["UserApi"],
    }),

    // Change password
    changePassword: builder.mutation({
      query: (data) => ({
        url: "/users/change-password",
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["UserApi"],
    }),
    getDashboardData: builder.query({
      query: () => ({
        url: `/dashboard`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["Users"],
    }),
    getMyStatus: builder.query({
      query: () => ({
        url: `/users/status`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["Users"],
    }),
  }),
});

export const {
  useRegisterUserMutation,
  useLoginUserMutation,
  useGetAllUsersQuery,
  useGetUserByIdQuery,
  useAddUserMutation,
  useUpdateUserMutation,
  useEditUserMutation,
  useDeleteUserMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useLazyGetAllUsersQuery,
  useLazyGetUserByIdQuery,
  useGetDashboardDataQuery,
  useGetMyStatusQuery,
} = UserApi;
